import React, { useEffect, useState } from "react";
import { ArrowRight, UserRound } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { Btn, Eyebrow, Section, SectionTitle, Lead } from "@/components/ui-kit/Kit";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";
import { fetchCommittee } from "@/lib/api";

export const CommitteePreview = () => {
  const { t } = useI18n();
  const [items, setItems] = useState([]);

  useEffect(() => {
    let alive = true;
    fetchCommittee()
      .then((d) => alive && setItems((d.items || []).slice(0, 6)))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  return (
    <Section className="py-24 sm:py-32" data-testid="committee-preview-section">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <Reveal>
          <Eyebrow>{t("home.committee.eyebrow")}</Eyebrow>
          <SectionTitle className="mt-6">{t("home.committee.title")}</SectionTitle>
        </Reveal>
        <Reveal delay={0.1}>
          <Btn to="/committees" variant="outline" data-testid="view-all-committees">
            {t("cta.viewAllCommittees")} <ArrowRight className="h-4 w-4" />
          </Btn>
        </Reveal>
      </div>
      <Reveal delay={0.08}>
        <Lead className="mt-5 max-w-xl">{t("home.committee.sub")}</Lead>
      </Reveal>

      {items.length === 0 ? (
        <Reveal delay={0.12}>
          <div
            className="mt-12 grid gap-px border border-navy/10 bg-navy/10 sm:grid-cols-2 lg:grid-cols-4"
            data-testid="committee-placeholders"
          >
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="bg-white p-8">
                <div className="flex h-14 w-14 items-center justify-center rounded-full border border-dashed border-navy/20">
                  <UserRound className="h-6 w-6 text-navy/20" strokeWidth={1.4} />
                </div>
                <p className="mt-7 font-mono text-[10px] uppercase tracking-[0.2em] text-navy/30">
                  Awaiting official publication
                </p>
                <div className="mt-4 h-2 w-3/4 bg-navy/5" />
                <div className="mt-2 h-2 w-1/2 bg-navy/5" />
              </div>
            ))}
          </div>
        </Reveal>
      ) : (
        <Stagger className="mt-12 grid gap-px border border-navy/10 bg-navy/10 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((p, i) => (
            <StaggerItem key={p.name + i} className="bg-white">
              <div className="p-8" data-testid={`committee-member-${i + 1}`}>
                {p.photo ? (
                  <img src={p.photo} alt={p.name} loading="lazy" className="h-20 w-20 rounded-full object-cover" />
                ) : (
                  <div className="flex h-20 w-20 items-center justify-center rounded-full bg-navy/5">
                    <UserRound className="h-8 w-8 text-navy/30" strokeWidth={1.3} />
                  </div>
                )}
                <h3 className="mt-6 font-display text-lg font-semibold text-navy">{p.name}</h3>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-grn">{p.position}</p>
                {p.district && <p className="mt-2 text-[12px] text-slate-500">{p.district}</p>}
                {p.profile && <p className="mt-4 text-sm leading-relaxed text-slate-600">{p.profile}</p>}
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      )}
    </Section>
  );
};
